import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import type { FormValues } from '../types';
import {
  validateBriefingProducts,
  type BriefingProductValidationItem,
} from '../services/briefingPromocoesApi';

const CHILD_FIELD_SUFFIX_RE = /__child(\d+)$/;
const DIRECT_PRODUCT_FIELD_KEYS = new Set([
  'eansObrigatorios',
  'skusObrigatorios',
  'eansInclusao',
  'eansExclusao',
  'skusInclusao',
  'skusExclusao',
  'skusFamilia',
]);
const CONDITIONAL_PRODUCT_FIELD_KEYS: Record<string, string> = {
  itensIncluidos: 'tipoItemIncluido',
  itensExcluidos: 'tipoItemExcluido',
};
const PRODUCT_TYPE_VALUES = new Set(['1', '2']);
const PRODUCT_CODE_SPLIT_RE = /[\n\r\t,;|]+/;

export interface ProductValidationState {
  enabled: boolean;
  loading: boolean;
  error: string | null;
  requested: string[];
  products: BriefingProductValidationItem[];
  missing: string[];
  productByCode: Map<string, BriefingProductValidationItem>;
  missingSet: Set<string>;
}

const emptyState: ProductValidationState = {
  enabled: false,
  loading: false,
  error: null,
  requested: [],
  products: [],
  missing: [],
  productByCode: new Map(),
  missingSet: new Set(),
};

export function normalizeProductCode(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

export function parseProductCodes(value: string | string[] | undefined): string[] {
  const rawValues = Array.isArray(value)
    ? value.flatMap((item) => String(item ?? '').split(PRODUCT_CODE_SPLIT_RE))
    : String(value ?? '').split(PRODUCT_CODE_SPLIT_RE);

  const unique: string[] = [];
  const seen = new Set<string>();

  rawValues.forEach((raw) => {
    const text = String(raw ?? '').trim();
    const normalized = normalizeProductCode(text);
    if (!text || !normalized || seen.has(normalized)) return;

    seen.add(normalized);
    unique.push(text);
  });

  return unique;
}

function baseFieldKey(fieldKey: string): string {
  return fieldKey.replace(CHILD_FIELD_SUFFIX_RE, '');
}

function childSiblingFieldKey(fieldKey: string, siblingBaseKey: string): string {
  const match = fieldKey.match(CHILD_FIELD_SUFFIX_RE);
  return match ? `${siblingBaseKey}__child${match[1]}` : siblingBaseKey;
}

export function shouldValidateProducts(fieldKey: string, values: FormValues): boolean {
  const baseKey = baseFieldKey(fieldKey);

  if (DIRECT_PRODUCT_FIELD_KEYS.has(baseKey)) return true;

  const productTypeField = CONDITIONAL_PRODUCT_FIELD_KEYS[baseKey];
  if (!productTypeField) return false;

  const isChildField = CHILD_FIELD_SUFFIX_RE.test(fieldKey);
  const siblingKey = childSiblingFieldKey(fieldKey, productTypeField);
  const productTypeValue = isChildField ? values[siblingKey] : values[productTypeField];

  return PRODUCT_TYPE_VALUES.has(String(productTypeValue ?? ''));
}

export function useProductValidation(
  value: string | string[] | undefined,
  fieldKey: string,
  values: FormValues
): ProductValidationState {
  const enabled = shouldValidateProducts(fieldKey, values);
  const requested = useMemo(() => (enabled ? parseProductCodes(value) : []), [enabled, value]);
  const requestedKey = requested.map(normalizeProductCode).join('\n');
  const [state, setState] = useState<Omit<ProductValidationState, 'enabled' | 'productByCode' | 'missingSet'>>({
    loading: false,
    error: null,
    requested: [],
    products: [],
    missing: [],
  });

  useEffect(() => {
    if (!enabled || requested.length === 0) {
      setState({
        loading: false,
        error: null,
        requested: [],
        products: [],
        missing: [],
      });
      return;
    }

    let active = true;
    setState((previous) => ({
      ...previous,
      loading: true,
      error: null,
      requested,
    }));

    const timer = window.setTimeout(async () => {
      try {
        const result = await validateBriefingProducts(requested);
        if (!active) return;

        setState({
          loading: false,
          error: null,
          requested: result.requested,
          products: result.found,
          missing: result.missing,
        });
      } catch (error) {
        if (!active) return;

        setState({
          loading: false,
          error: error instanceof Error ? error.message : 'Nao foi possivel validar os produtos.',
          requested,
          products: [],
          missing: [],
        });
      }
    }, 450);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [enabled, requestedKey]);

  const productByCode = useMemo(() => {
    const productsMap = new Map<string, BriefingProductValidationItem>();

    state.products.forEach((product) => {
      [product.ean, product.sku].forEach((value) => {
        const code = normalizeProductCode(value);
        if (code) productsMap.set(code, product);
      });
    });

    return productsMap;
  }, [state.products]);

  const missingSet = useMemo(
    () => new Set(state.missing.map(normalizeProductCode)),
    [state.missing]
  );

  return {
    ...emptyState,
    ...state,
    enabled,
    productByCode,
    missingSet,
  };
}

function formatProductMeta(product: BriefingProductValidationItem): string {
  return [product.nomeMarca, product.nomeIndustria, product.categoria, product.grupo, product.subGrupo]
    .filter(Boolean)
    .join(' | ');
}

function ProductImage({ product, size = 48 }: { product: BriefingProductValidationItem; size?: number }) {
  const [hasImageError, setHasImageError] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const canPreviewImage = Boolean(product.foto && !hasImageError);

  return (
    <Box
      onMouseEnter={() => setIsPreviewOpen(true)}
      onMouseLeave={() => setIsPreviewOpen(false)}
      onFocus={() => setIsPreviewOpen(true)}
      onBlur={() => setIsPreviewOpen(false)}
      tabIndex={canPreviewImage ? 0 : -1}
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        cursor: canPreviewImage ? 'zoom-in' : 'default',
        outline: 'none',
      }}
    >
      {canPreviewImage ? (
        <Box
          component="img"
          src={product.foto}
          alt={product.nomeProduto || product.ean}
          loading="lazy"
          onError={() => setHasImageError(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            p: 0.5,
          }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          SKU
        </Typography>
      )}
      {canPreviewImage && isPreviewOpen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(15, 23, 42, 0.36)',
            p: 2,
          }}
        >
          <Box
            sx={{
              width: 'min(520px, calc(100vw - 32px))',
              maxHeight: 'calc(100vh - 64px)',
              bgcolor: '#fff',
              borderRadius: 1.25,
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.28)',
              border: '1px solid rgba(0, 158, 247, 0.22)',
              p: 2,
            }}
          >
            <Box
              component="img"
              src={product.foto}
              alt={product.nomeProduto || product.ean}
              sx={{
                width: '100%',
                maxHeight: 360,
                objectFit: 'contain',
                display: 'block',
                bgcolor: '#fff',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                color: 'text.primary',
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {product.nomeProduto || 'Produto sem nome'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
              {product.ean}
              {product.sku && product.sku !== product.ean ? ` · SKU ${product.sku}` : ''}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ProductCard({ product, large = false }: { product: BriefingProductValidationItem; large?: boolean }) {
  const meta = formatProductMeta(product);

  return (
    <Box
      sx={{
        minWidth: 0,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        p: large ? 1 : 0.75,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'rgba(0, 158, 247, 0.18)',
        bgcolor: '#fff',
      }}
    >
      <ProductImage product={product} size={large ? 58 : 48} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          display="block"
          sx={{
            color: 'text.primary',
            fontWeight: 700,
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: large ? 3 : 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {product.nomeProduto || 'Produto sem nome'}
        </Typography>
        <Typography variant="caption" display="block" sx={{ color: 'primary.main', fontWeight: 700 }}>
          {product.ean}
          {product.sku && product.sku !== product.ean ? ` · SKU ${product.sku}` : ''}
        </Typography>
        {meta && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {meta}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function ProductValidationPreview({ validation }: { validation: ProductValidationState }) {
  const [isProductsDialogOpen, setIsProductsDialogOpen] = useState(false);

  if (!validation.enabled || validation.requested.length === 0) return null;

  const visibleProducts = validation.products.slice(0, 8);
  const hiddenProducts = Math.max(validation.products.length - visibleProducts.length, 0);
  const visibleMissing = validation.missing.slice(0, 8);
  const hiddenMissing = Math.max(validation.missing.length - visibleMissing.length, 0);
  const hasMissing = validation.missing.length > 0;
  const hasProducts = validation.products.length > 0;

  return (
    <>
      <Box
        sx={{
          mt: 1,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f7fbff',
        }}
      >
      {validation.loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Consultando produtos...
          </Typography>
        </Box>
      )}

      {validation.error && !validation.loading && (
        <Typography variant="caption" color="warning.main" fontWeight={600}>
          Nao foi possivel validar estes SKUs/EANs agora.
        </Typography>
      )}

      {!validation.loading && !validation.error && (
        <>
          <Typography
            variant="caption"
            color={hasProducts ? 'success.main' : 'text.secondary'}
            fontWeight={700}
            display="block"
          >
            {hasProducts
              ? `${validation.products.length} SKU/EAN encontrado(s)`
              : 'Nenhum SKU/EAN encontrado'}
            {hasMissing && hasProducts ? (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {` · ${validation.missing.length} nao encontrado(s)`}
              </Box>
            ) : null}
          </Typography>

          {visibleProducts.length > 0 && (
            <Box
              sx={{
                mt: 0.75,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 0.75,
              }}
            >
              {visibleProducts.map((product) => (
                <ProductCard key={`${product.ean}-${product.sku || ''}`} product={product} />
              ))}
            </Box>
          )}

          {hiddenProducts > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={() => setIsProductsDialogOpen(true)}
              sx={{ mt: 0.5, px: 0, minWidth: 0, fontWeight: 700, textTransform: 'none' }}
            >
              Ver todos os {validation.products.length} produtos
            </Button>
          )}

          {hasMissing && (
            <Box sx={{ mt: 0.75 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Nao encontrado(s):{' '}
              </Typography>
              {visibleMissing.map((item, index) => (
                <Box
                  key={`${item}-${index}`}
                  component="span"
                  sx={{
                    display: 'inline-block',
                    mr: 0.5,
                    mb: 0.25,
                    px: 0.5,
                    borderRadius: 0.75,
                    color: 'error.main',
                    bgcolor: '#fff1f1',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    lineHeight: 1.55,
                  }}
                >
                  {item}
                </Box>
              ))}
              {hiddenMissing > 0 && (
                <Typography variant="caption" color="error.main" fontWeight={700}>
                  +{hiddenMissing}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
      </Box>

      <Dialog
        open={isProductsDialogOpen}
        onClose={() => setIsProductsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Produtos encontrados
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {validation.products.length} encontrado(s)
                {hasMissing ? ` · ${validation.missing.length} nao encontrado(s)` : ''}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setIsProductsDialogOpen(false)}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Fechar
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 1,
              maxHeight: '62vh',
              overflowY: 'auto',
              pr: 0.5,
            }}
          >
            {validation.products.map((product) => (
              <ProductCard key={`dialog-${product.ean}-${product.sku || ''}`} product={product} large />
            ))}
          </Box>

          {hasMissing && (
            <Box
              sx={{
                mt: 2,
                p: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'rgba(241, 65, 108, 0.22)',
                bgcolor: '#fff8fa',
              }}
            >
              <Typography variant="caption" color="error.main" fontWeight={800} display="block" sx={{ mb: 0.75 }}>
                SKUs/EANs nao encontrados
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 118, overflowY: 'auto' }}>
                {validation.missing.map((item, index) => (
                  <Box
                    key={`dialog-missing-${item}-${index}`}
                    component="span"
                    sx={{
                      display: 'inline-block',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.75,
                      color: 'error.main',
                      bgcolor: '#fff1f1',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
